import { useMemo, useState } from "react";
import { RadialBarChart, RadialBar, ResponsiveContainer, PolarAngleAxis } from "recharts";
import { useSEO } from "../../lib/seo";
import { Link } from "react-router-dom";

const fields = [
  { key: "hours", label: "Working hours per day", min: 4, max: 12, step: 0.5, def: 8 },
  { key: "stopMins", label: "Minutes per stop (service time)", min: 10, max: 60, step: 1, def: 28 },
  { key: "driveMins", label: "Drive minutes between stops", min: 2, max: 30, step: 1, def: 9 },
  { key: "days", label: "Route days per week", min: 1, max: 6, step: 1, def: 5 },
  { key: "rate", label: "Avg monthly rate per pool ($)", min: 50, max: 500, step: 5, def: 180 },
  { key: "current", label: "Pools currently assigned to this tech", min: 0, max: 150, step: 1, def: 52 },
];

export default function CapacityEstimator() {
  const [v, setV] = useState(Object.fromEntries(fields.map((f) => [f.key, f.def])));

  useSEO({
    title: "Technician Capacity Estimator for Pool Service",
    description: "Free tool: calculate how many pools each technician can service per day and week based on stop time, drive time and schedule.",
  });

  const r = useMemo(() => {
    const minsPerDay = v.hours * 60;
    const minsPerStop = v.stopMins + v.driveMins;
    const stopsPerDay = Math.floor(minsPerDay / minsPerStop);
    const poolsPerWeek = stopsPerDay * v.days;
    const utilization = poolsPerWeek > 0 ? Math.min((v.current / poolsPerWeek) * 100, 150) : 0;
    const revenuePotential = poolsPerWeek * v.rate;
    return { stopsPerDay, poolsPerWeek, utilization, revenuePotential, headroom: poolsPerWeek - v.current };
  }, [v]);

  const gaugeColor = r.utilization > 90 ? "#EF4444" : r.utilization > 75 ? "#F59E0B" : "#10B981";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-16" data-testid="capacity-estimator-page">
      <nav className="text-xs text-slate-400 mb-6"><Link to="/tools" className="hover:text-sky-600">Tools</Link> / Technician Capacity</nav>
      <h1 className="font-heading text-4xl sm:text-5xl font-black tracking-tight text-slate-900">Technician Capacity Estimator</h1>
      <p className="mt-4 text-slate-600 max-w-2xl leading-relaxed">Know exactly when your tech is full — and when it's time to hire.</p>

      <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-10">
        <div className="bg-white rounded-2xl border border-border p-8 space-y-7">
          {fields.map((f) => (
            <div key={f.key}>
              <div className="flex justify-between items-baseline mb-2">
                <label className="text-sm font-semibold text-slate-700">{f.label}</label>
                <span className="font-heading font-bold text-sky-600" data-testid={`capacity-value-${f.key}`}>{v[f.key]}</span>
              </div>
              <input
                type="range"
                data-testid={`capacity-input-${f.key}`}
                min={f.min} max={f.max} step={f.step}
                value={v[f.key]}
                onChange={(e) => setV({ ...v, [f.key]: parseFloat(e.target.value) })}
                className="w-full accent-sky-500"
              />
            </div>
          ))}
        </div>

        <div className="space-y-6">
          <div className="bg-white rounded-2xl border border-border p-6">
            <h2 className="font-heading font-bold text-slate-900 text-sm mb-2">Capacity utilization</h2>
            <ResponsiveContainer width="100%" height={200}>
              <RadialBarChart innerRadius="70%" outerRadius="100%" data={[{ value: Math.min(r.utilization, 100) }]} startAngle={180} endAngle={0}>
                <PolarAngleAxis type="number" domain={[0, 100]} tick={false} />
                <RadialBar dataKey="value" fill={gaugeColor} cornerRadius={10} background={{ fill: "#F1F5F9" }} />
              </RadialBarChart>
            </ResponsiveContainer>
            <p className="text-center font-heading font-black text-3xl -mt-16 mb-6" style={{ color: gaugeColor }} data-testid="capacity-result-utilization">
              {r.utilization.toFixed(0)}%
            </p>
            <p className="text-center text-sm text-slate-500">
              {r.utilization > 90 ? "Over capacity — time to hire or rebalance routes." : r.utilization > 75 ? "Getting full. Start planning your next hire." : "Healthy headroom for growth."}
            </p>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Stat label="Max stops / day" value={r.stopsPerDay} testId="capacity-result-stopsday" />
            <Stat label="Max pools / week" value={r.poolsPerWeek} testId="capacity-result-poolsweek" />
            <Stat label="Open capacity (pools)" value={r.headroom} testId="capacity-result-headroom" />
            <Stat label="Revenue potential / mo" value={`$${r.revenuePotential.toLocaleString()}`} testId="capacity-result-revenue" />
          </div>
          <div className="bg-sky-50 border border-sky-100 rounded-2xl p-6 text-sm text-slate-700 leading-relaxed">
            <strong className="font-heading">Hiring rule:</strong> hire when a tech sustains 90%+ utilization for 6 weeks. More in our <Link to="/guides/technician-scheduling-guide" className="text-sky-600 underline">scheduling guide</Link>.
          </div>
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, testId }) {
  return (
    <div className="rounded-2xl border border-border bg-white p-5">
      <p className="text-xs uppercase tracking-[0.15em] font-semibold text-slate-400">{label}</p>
      <p className="font-heading font-black text-2xl mt-1 text-slate-900" data-testid={testId}>{value}</p>
    </div>
  );
}
